/**
 * Duplicate of CallPane created to prevent regression issues.
 *
 * Due to the significant changes introduced by the Outbound SMS update, this file is a temporary copy of the original CallPane component.
 * Modifications will be made here to isolate and stabilize the new functionality without affecting the existing implementation.
 *
 * Note: Both this file and the original CallPane must be kept in sync until this version is stable enough to fully replace the original.
 * The goal is to eventually replace the old CallPane with this version once the Outbound SMS changes are fully tested and stable.
 */
import { useState, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { withTheme } from 'styled-components';
import { XCloseIcon } from '@rsos/assets/icons';
import SplitPane from '@rsos/components/capstone/DataCards/SplitPane';
import CallSource from '@rsos/components/capstone/Location/CallSource';
import { getDataSource } from '@rsos/components/capstone/Location/helpers';
import LocationList from '@rsos/components/capstone/LocationList';
import { Box } from '@rsos/components/capstone/base';
import { LayoutContext } from '@rsos/components/capstone/base/Layout';
import { BP_LAPTOP } from '@rsos/constants/breakpoints';
import useHasLocationsToRender from '@rsos/hooks/useHasLocationsToRender';
import useIsTF911Enabled from '@rsos/hooks/useIsTF911Enabled';
import { expandAllChatsForCall } from '@rsos/reduxDataStore/src/features/chats/chatsSlice';
import { useOpenChatTypes } from '@rsos/reduxDataStore/src/features/chats/hooks';
import { useSelectedTextFrom911Conversation } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Selectors';
import { getMostRecentLocations } from '@rsos/utils/getMostRecentLoc';
import { formatInternationalPhoneNumberHuman } from '@rsos/utils/internationalPhoneNumberUtils';
import useIsIRP from '@rsos/utils/useIsIRP';
import useFetchEmergencySessionForTel from '../../../hooks/useFetchEmergencySessionForTel';
import useShouldRenderCallSplitPane, {
  useIsAnyChatEnabled,
} from '../../../hooks/useShouldRenderCallSplitPane';
import useShouldRenderTF911 from '../../../hooks/useShouldRenderTF911';
import ADRContainer from '../../AdditionalData/ADRContainer';
import Media from '../../AdditionalData/AdrQuery/Media';
import AgencyShareChatButton from '../../AgencyShareChat/AgencyShareChatButton';
import CopyAllButton from '../../CopyAllButton';
import OnsitePersonnelBanner from '../../SpatialLayers/OnsitePersonnelBanner';
import What3WordsSearchResult from '../../What3Words/What3WordsSearchResult';
import { CHAT_HEADER_HEIGHT } from '../CallChats/AgencyShareChat/ASCChatBox.styles';
import CallChats from '../CallChats/CallChats';
import { TF911_CHAT_HEADER_HEIGHT } from '../CallChats/TF911ChatBox/constants';
import BPLaptopHeader from '../CallPane/BPLaptopHeader';
import {
  PANE_HEADER_HEIGHT,
  StyledCallPane,
  StyledHeader,
  StyledCallerID,
  CloseButton,
  Tools,
} from '../CallPane/CallPane.styles';
import { OUTBOUND_SMS_BANNER_HEIGHT } from './CallPaneNext.styles';
import { DidNotCallBanner } from './DidNotCallBanner';

const CallPaneNext = withTheme(
  ({
    height,
    onActionableButton = () => {},
    onClose = () => {},
    theme,
    hasW3w,
  }) => {
    const dispatch = useDispatch();
    const { layout } = useContext(LayoutContext);

    const [accordionHeight, setAccordionHeight] = useState(0);

    const callerID = useSelector(
      state => state.emergencyCalls.selectedCallerID,
    );

    const currentCall = useSelector(
      state => state.emergencyCalls.callQueue[callerID],
    );

    const isCallShared = useSelector(
      state =>
        state.emergencyCalls.callQueue[callerID]?.agencyShareChat?.isCallShared,
    );

    const isCallReceived = useSelector(
      state =>
        state.emergencyCalls.callQueue[callerID]?.agencyShareChat
          ?.isCallReceived,
    );

    useFetchEmergencySessionForTel(callerID);

    const { mostRecentLocation, mostRecentSecondaryLocation } =
      getMostRecentLocations(currentCall);

    const source = getDataSource(
      mostRecentLocation || mostRecentSecondaryLocation,
    );

    const isAllCallsSelected = useSelector(
      state => !state.emergencyCalls.selectedCallerID,
    );

    const isIRPresponsiveConditional = useIsIRP()?.isIRPresponsiveConditional;

    const hasLocationsToRender = useHasLocationsToRender();
    const textFrom911Conversation =
      useSelectedTextFrom911Conversation(callerID);
    const isOutboundSMSNumber = useMemo(
      () => !!textFrom911Conversation?.data?.isOutboundSMS,
      [textFrom911Conversation],
    );

    const isA911Call = useMemo(
      () => hasLocationsToRender,
      [hasLocationsToRender],
    );

    const displayPhoneNumber = formatInternationalPhoneNumberHuman(callerID);

    const availableHeight = useMemo(() => {
      const defaultHeight = height - PANE_HEADER_HEIGHT - accordionHeight;
      if (isOutboundSMSNumber && !hasLocationsToRender) {
        return defaultHeight - OUTBOUND_SMS_BANNER_HEIGHT;
      }
      return defaultHeight;
    }, [height, accordionHeight, isOutboundSMSNumber, hasLocationsToRender]);

    // This is for the FREE TF911 module
    const isTF911Enabled = useIsTF911Enabled();
    // This checks whether to render TF911 or not, based on whether the call is ANI/ALI with specific classes of service
    const shouldRenderTF911 = useShouldRenderTF911(callerID);

    const shouldRenderSplitPane = useShouldRenderCallSplitPane();

    const isAnyChatEnabled = useIsAnyChatEnabled();

    const isAgencyShareChatChatBoxVisible = useOpenChatTypes().agencyShare;
    const isTextFrom911ChatBoxVisible = useOpenChatTypes().textFrom911;

    const maxChatsCount = useMemo(() => {
      let count = 0;
      if (isTF911Enabled && shouldRenderTF911) {
        count += 1;
      }
      if (isCallReceived || isCallShared) {
        count += 1;
      }
      return count;
    }, [isCallReceived, isCallShared, isTF911Enabled, shouldRenderTF911]);

    // Tracking the count when there are only two things seems bonkers to me,
    // but I'm not gonna re-architect this component, I'm just culling code
    // that's been made redundant. So, uh, sorry for this:
    const collapsedChatsCount = Math.min(
      [!isAgencyShareChatChatBoxVisible, !isTextFrom911ChatBoxVisible].filter(
        x => x,
      ).length,
      maxChatsCount,
    );

    const handleOuterPrimaryPaneChange = () => {
      if (!isAgencyShareChatChatBoxVisible && !isTextFrom911ChatBoxVisible) {
        dispatch(expandAllChatsForCall(callerID));
      }
    };

    // TODO: future improvement. account for <Media/>
    const p2MaxHeight = useMemo(() => {
      if (collapsedChatsCount === 0) {
        return availableHeight;
      } else if (collapsedChatsCount === maxChatsCount) {
        if (!isAgencyShareChatChatBoxVisible && !isTextFrom911ChatBoxVisible) {
          return `${CHAT_HEADER_HEIGHT + TF911_CHAT_HEADER_HEIGHT + 10}px`;
        } else if (!isAgencyShareChatChatBoxVisible) {
          return `${CHAT_HEADER_HEIGHT}px`;
        } else if (!isTextFrom911ChatBoxVisible) {
          return `${TF911_CHAT_HEADER_HEIGHT}px`;
        }
      } else {
        if (isAgencyShareChatChatBoxVisible) {
          return CHAT_HEADER_HEIGHT + availableHeight / 2;
        } else if (isTextFrom911ChatBoxVisible) {
          return TF911_CHAT_HEADER_HEIGHT + availableHeight / 2;
        }
        return availableHeight;
      }
    }, [
      availableHeight,
      collapsedChatsCount,
      isAgencyShareChatChatBoxVisible,
      isTextFrom911ChatBoxVisible,
      maxChatsCount,
    ]);

    const isSplitPaneDisabled = useMemo(() => {
      return !!(
        !shouldRenderSplitPane ||
        (isOutboundSMSNumber &&
          !hasLocationsToRender &&
          !!currentCall &&
          !Object.keys(currentCall?.additionalData)?.length)
      );
    }, [
      shouldRenderSplitPane,
      isOutboundSMSNumber,
      hasLocationsToRender,
      currentCall,
    ]);

    return (
      <StyledCallPane data-name="event-pane">
        {hasW3w && (
          <What3WordsSearchResult
            isAllCallsSelected={isAllCallsSelected}
            hasW3w={hasW3w}
            setAccordionHeight={setAccordionHeight}
            accordionHeight={accordionHeight}
          />
        )}
        {layout?.window?.width <= BP_LAPTOP && !isIRPresponsiveConditional ? (
          <BPLaptopHeader
            onClose={onClose}
            formattedPhone={displayPhoneNumber}
            source={source}
          />
        ) : (
          <>
            <StyledHeader data-name="event-pane-header">
              <Box align="center" horizontal justify="space-between">
                <StyledCallerID data-name="event-pane-caller-id">
                  {displayPhoneNumber}
                </StyledCallerID>

                <Tools align="center" horizontal data-name="event-pane-tools">
                  <AgencyShareChatButton />

                  {isA911Call && <CopyAllButton />}
                  <CloseButton onClick={onClose} data-name="close-button">
                    <XCloseIcon />
                  </CloseButton>
                </Tools>
              </Box>
              {isA911Call && (
                <Box>
                  <CallSource source={source} />
                </Box>
              )}

              {isOutboundSMSNumber && !hasLocationsToRender && (
                <Box mt="10px">
                  <DidNotCallBanner />
                </Box>
              )}
            </StyledHeader>
          </>
        )}

        <SplitPane
          disabled={isSplitPaneDisabled}
          paneStyle={{
            background: `${theme.colors.background}`,
            borderRadius: '5px',
            overflowY: 'hidden',
            position: 'relative',
          }}
          pane2Style={{
            maxHeight: p2MaxHeight,
          }}
          primary="second"
          height={availableHeight}
          onChange={handleOuterPrimaryPaneChange}
        >
          <SplitPane
            height={availableHeight}
            disabled
            // moved Historic Locations to a dropdown and Resizer is no longer needed
            primary="second"
            paneStyle={{
              border: `1px solid ${theme.colors.lightLineDivider}`,
              overflowY: 'auto',
              position: 'relative',
            }}
          >
            {isA911Call && <LocationList />}

            {isA911Call && <OnsitePersonnelBanner callerId={callerID} />}

            <ADRContainer onActionableButton={onActionableButton} />
          </SplitPane>
          <>
            {isA911Call && <Media />}

            {isAnyChatEnabled && <CallChats />}
          </>
        </SplitPane>
      </StyledCallPane>
    );
  },
);

export default CallPaneNext;
